import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import axios from "axios";

export interface UploadProps {
  onUpload: () => void;
}

export interface PlanIprSheetLayer {
  layerNumber: number;
  iprSheetPath: string;
  attachmentsSheetPath: string;
  attachments: number[];
  ipr: string[];
  /** For layer 2: start aligner number from API. */
  layer2AlignerNo?: number;
}

interface WorkspacePlan {
  movementOverviewPdfPath?: string;
  planNo: number;
  detailedPlanType: string;
  uploadedPdfPath?: string;
  countryIsoCode: string;
  planIprSheetData?: PlanIprSheetLayer[];
  upperAlignerTotal?: number;
  lowerAlignerTotal?: number;
  /** Layer 2 start aligner number (plan-level in zenyum API). */
  layer2AlignerNo?: number;
  patientName?: string;
}

export const Upload = (props: UploadProps) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File>();
  const [pdfFile, setPdfFile] = useState<File>();
  const [mobileOrUid, setMobileOrUid] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>();
  const pdfFileInputRef = useRef<HTMLInputElement>();
  const [uploadStatus, setUploadStatus] = useState<"ZIP" | "PDF">();
  const [isWorkspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [workspaceUid, setWorkspaceUid] = useState("");
  const [workspacePlans, setWorkspacePlans] = useState<WorkspacePlan[]>([]);
  const [selectedWorkspacePlan, setSelectedWorkspacePlan] =
    useState<WorkspacePlan | null>(null);
  const [workspaceZipFile, setWorkspaceZipFile] = useState<File>();
  const [workspacePdfFile, setWorkspacePdfFile] = useState<File>();
  const [isWorkspaceSearching, setWorkspaceSearching] = useState(false);
  const [isWorkspaceSubmitting, setWorkspaceSubmitting] = useState(false);
  const [workspaceUidError, setWorkspaceUidError] = useState("");
  const [hasWorkspaceSearched, setHasWorkspaceSearched] = useState(false);
  const [hasWorkspaceSearchSuccess, setHasWorkspaceSearchSuccess] =
    useState(false);
  const workspaceZipInputRef = useRef<HTMLInputElement>();
  const workspacePdfInputRef = useRef<HTMLInputElement>();
  const hasHandledWorkspaceQueryRef = useRef(false);

  const isZenyum = process.env.NAME === "zenyum";

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFile(file);
  };

  const handlePdfFileChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfFile(file);
  };

  const handleTextChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setMobileOrUid(e.target.value);
  };

  const handleWorkspaceUidChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    setWorkspaceUid(e.target.value);
    if (workspaceUidError) setWorkspaceUidError("");
  };

  const handleWorkspaceZipChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const file = e.target.files[0];
    if (!file) return;
    setWorkspaceZipFile(file);
  };

  const handleWorkspacePdfChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const file = e.target.files[0];
    if (!file) return;
    setWorkspacePdfFile(file);
  };

  const fetchWorkspacePlans = async () => {
    const trimmedUid = workspaceUid.trim();
    await fetchWorkspacePlansWithUid(trimmedUid);
  };

  const fetchWorkspacePlansWithUid = async (
    trimmedUid: string,
    planNoFromQuery?: string,
  ) => {
    if (!trimmedUid) return;
    if (!trimmedUid.startsWith("ZYN-")) {
      setWorkspaceUidError("UID is invalid");
      setHasWorkspaceSearched(false);
      return;
    }
    setWorkspaceSearching(true);
    setWorkspacePlans([]);
    setSelectedWorkspacePlan(null);
    setHasWorkspaceSearched(true);
    setHasWorkspaceSearchSuccess(false);
    try {
      const { data } = await axios.get(
        `${process.env.API_PREFIX}/zenyum-plans`,
        {
          params: { uid: trimmedUid },
        },
      );

      const plans = Array.isArray(data?.plans)
        ? data.plans
        : Array.isArray(data)
          ? data
          : [];
      if (Array.isArray(plans) && plans.length > 0) {
        setWorkspacePlans(plans);
        const matchedPlan = planNoFromQuery
          ? plans.find(
              (plan) => `${plan.planNo}` === `${planNoFromQuery}`.trim(),
            )
          : null;
        setSelectedWorkspacePlan(matchedPlan ?? plans[0]);
        setHasWorkspaceSearchSuccess(true);
      }
    } catch (err) {
      console.log(err?.response?.data || err?.message);
    } finally {
      setWorkspaceSearching(false);
    }
  };

  const handleUpload = async () => {
    if (!pdfFile || !file) {
      alert("Please upload both files");
    }
    if (!file || !mobileOrUid || !pdfFile) return;
    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${process.env.API_PREFIX}/upload-init`,
        {
          mobileOrUid: mobileOrUid?.trim(),
        },
      );
      if (data?.zipFileUploadUrl) {
        setUploadStatus("ZIP");
        await axios.put(data.zipFileUploadUrl, file, {
          headers: { "Content-Type": "application/zip" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.ceil(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            if (percentCompleted % 10 === 0)
              setUploadProgress(percentCompleted);
          },
        });
        setUploadProgress(0);
        setUploadStatus(undefined);
      }

      if (data?.pdfFileUploadUrl) {
        setUploadStatus("PDF");
        await axios.put(data.pdfFileUploadUrl, pdfFile, {
          headers: { "Content-Type": "application/pdf" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.ceil(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            if (percentCompleted % 10 === 0)
              setUploadProgress(percentCompleted);
          },
        });
        setUploadProgress(0);
        setUploadStatus(undefined);
      }

      if (data?.exportationId)
        await axios.patch(`${process.env.API_PREFIX}/update-uploaded-status`, {
          exportationId: data.exportationId,
        });
      props.onUpload();
    } catch (err) {
      console.log(err?.response?.data || err?.message);
    } finally {
      setSubmitting(false);
      fileInputRef.current.value = "";
      pdfFileInputRef.current.value = "";
      setFile(undefined);
      setMobileOrUid("");
    }
  };

  const resetWorkspaceState = () => {
    setWorkspaceUid("");
    setWorkspacePlans([]);
    setSelectedWorkspacePlan(null);
    setWorkspaceZipFile(undefined);
    setWorkspacePdfFile(undefined);
    setUploadProgress(0);
    setUploadStatus(undefined);
    setWorkspaceUidError("");
    setHasWorkspaceSearched(false);
    setHasWorkspaceSearchSuccess(false);
    if (workspaceZipInputRef.current) {
      workspaceZipInputRef.current.value = "";
    }
    if (workspacePdfInputRef.current) {
      workspacePdfInputRef.current.value = "";
    }
  };

  const handleWorkspaceUpload = async () => {
    if (!workspaceZipFile || !selectedWorkspacePlan || !workspaceUid.trim()) {
      alert("Please provide UID, plan, and zip file");
      return;
    }
    const pdfRequired = !selectedWorkspacePlan.uploadedPdfPath?.trim();
    if (pdfRequired && !workspacePdfFile) {
      alert("Please upload a Report PDF file");
      return;
    }

    try {
      setWorkspaceSubmitting(true);
      const body: {
        movementOverviewPdfPath?: string;
        mobileOrUid: string;
        planNo: string;
        planName: string;
        countryCode: string;
        planIprSheetData?: PlanIprSheetLayer[];
        upperAlignerTotal?: number;
        lowerAlignerTotal?: number;
        layer2AlignerNo?: number;
        patientName?: string;
      } = {
        movementOverviewPdfPath: selectedWorkspacePlan.movementOverviewPdfPath,
        mobileOrUid: workspaceUid.trim(),
        planNo: `${selectedWorkspacePlan.planNo}`,
        planName: selectedWorkspacePlan.detailedPlanType,
        countryCode: selectedWorkspacePlan.countryIsoCode,
        patientName: selectedWorkspacePlan.patientName,
      };
      const planIprSheetData = selectedWorkspacePlan.planIprSheetData;
      if (Array.isArray(planIprSheetData) && planIprSheetData.length > 0) {
        body.planIprSheetData = planIprSheetData;
        if (
          typeof selectedWorkspacePlan.upperAlignerTotal === "number" &&
          typeof selectedWorkspacePlan.lowerAlignerTotal === "number"
        ) {
          body.upperAlignerTotal = selectedWorkspacePlan.upperAlignerTotal;
          body.lowerAlignerTotal = selectedWorkspacePlan.lowerAlignerTotal;
        }
        if (typeof selectedWorkspacePlan.layer2AlignerNo === "number") {
          body.layer2AlignerNo = selectedWorkspacePlan.layer2AlignerNo;
        }
      }
      const { data } = await axios.post(
        `${process.env.API_PREFIX}/upload-init`,
        body,
      );

      if (data?.zipFileUploadUrl) {
        setUploadStatus("ZIP");
        await axios.put(data.zipFileUploadUrl, workspaceZipFile, {
          headers: { "Content-Type": "application/zip" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.ceil(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            if (percentCompleted % 10 === 0)
              setUploadProgress(percentCompleted);
          },
        });
        setUploadProgress(0);
        setUploadStatus(undefined);
      }

      if (data?.pdfFileUploadUrl) {
        setUploadStatus("PDF");
        let pdfBody: Blob | File | undefined;
        if (workspacePdfFile) {
          pdfBody = workspacePdfFile;
        } else if (selectedWorkspacePlan.uploadedPdfPath) {
          const pdfResponse = await axios.get(
            selectedWorkspacePlan.uploadedPdfPath,
            {
              responseType: "blob",
            },
          );
          pdfBody = pdfResponse.data;
        }
        if (!pdfBody) {
          setUploadStatus(undefined);
        } else {
          await axios.put(data.pdfFileUploadUrl, pdfBody, {
            headers: { "Content-Type": "application/pdf" },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.ceil(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              if (percentCompleted % 10 === 0)
                setUploadProgress(percentCompleted);
            },
          });
          setUploadProgress(0);
          setUploadStatus(undefined);
        }
      }

      if (data?.exportationId)
        await axios.patch(`${process.env.API_PREFIX}/update-uploaded-status`, {
          exportationId: data.exportationId,
        });
      props.onUpload();
      setWorkspaceModalOpen(false);
      resetWorkspaceState();
    } catch (err) {
      console.log(err?.response?.data || err?.message);
    } finally {
      setWorkspaceSubmitting(false);
    }
  };

  const renderUploadProgress = () => {
    return uploadProgress > 0 ? (
      <div className="mt-4">
        {uploadStatus && (
          <div className="mb-2">
            <Label>Uploading {uploadStatus.toLowerCase()}</Label>
          </div>
        )}
        <Progress value={uploadProgress} />
      </div>
    ) : (
      <div>&nbsp;</div>
    );
  };

  useEffect(() => {
    if (!isZenyum) return;
    if (hasHandledWorkspaceQueryRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const uidParam = params.get("uid")?.trim() ?? "";
    const planNoParam = params.get("planNo")?.trim() ?? "";
    if (!uidParam || !planNoParam) return;

    hasHandledWorkspaceQueryRef.current = true;
    setWorkspaceModalOpen(true);
    setWorkspaceUid(uidParam);
    fetchWorkspacePlansWithUid(uidParam, planNoParam);

    params.delete("uid");
    params.delete("planNo");
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${
      nextSearch ? `?${nextSearch}` : ""
    }${window.location.hash}`;
    window.history.replaceState({}, "", nextUrl);
  }, [isZenyum]);

  return (
    <div className="max-w-3xl">
      {isZenyum && (
        <>
          <Button
            onClick={() => setWorkspaceModalOpen(true)}
            disabled={isWorkspaceSubmitting}
          >
            Upload from Workspace
          </Button>

          {isWorkspaceModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="w-full max-w-2xl rounded-md bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Workspace Upload</h2>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (isWorkspaceSubmitting) return;
                      setWorkspaceModalOpen(false);
                      resetWorkspaceState();
                    }}
                  >
                    Close
                  </Button>
                </div>

                <div className="grid gap-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="workspaceUid">UID</Label>
                    <Input
                      id="workspaceUid"
                      value={workspaceUid}
                      onChange={handleWorkspaceUidChange}
                      disabled={hasWorkspaceSearchSuccess}
                    />
                    {workspaceUidError && (
                      <span className="text-sm text-red-500">
                        {workspaceUidError}
                      </span>
                    )}
                  </div>

                  {!hasWorkspaceSearchSuccess && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={fetchWorkspacePlans}
                        disabled={isWorkspaceSearching}
                      >
                        Search
                      </Button>
                      {isWorkspaceSearching && (
                        <span className="text-sm text-muted-foreground">
                          Searching plans...
                        </span>
                      )}
                    </div>
                  )}

                  {hasWorkspaceSearched &&
                    !hasWorkspaceSearchSuccess &&
                    !isWorkspaceSearching && (
                      <p className="text-sm text-muted-foreground">
                        No plans found
                      </p>
                    )}

                  {hasWorkspaceSearchSuccess && (
                    <>
                      <div className="grid gap-1.5">
                        <Label htmlFor="workspacePlan">Plan</Label>
                        <select
                          id="workspacePlan"
                          value={selectedWorkspacePlan?.planNo ?? ""}
                          onChange={(event) => {
                            const nextPlan = workspacePlans.find(
                              (plan) => `${plan.planNo}` === event.target.value,
                            );
                            setSelectedWorkspacePlan(nextPlan ?? null);
                          }}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <option value="">Select plan</option>
                          {workspacePlans.map((plan) => (
                            <option key={plan.planNo} value={plan.planNo}>
                              {plan.detailedPlanType} (#{plan.planNo})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid gap-1.5">
                        <Label htmlFor="workspaceZipFile">PLY Zip File</Label>
                        <Input
                          id="workspaceZipFile"
                          type="file"
                          accept="application/zip"
                          onChange={handleWorkspaceZipChange}
                          ref={workspaceZipInputRef}
                        />
                      </div>

                      <div className="grid gap-1.5">
                        <Label htmlFor="workspacePdfFile">
                          Report PDF File
                          {selectedWorkspacePlan?.uploadedPdfPath?.trim()
                            ? " (optional)"
                            : ""}
                        </Label>
                        <Input
                          id="workspacePdfFile"
                          type="file"
                          accept="application/pdf"
                          onChange={handleWorkspacePdfChange}
                          ref={workspacePdfInputRef}
                        />
                      </div>

                      <div className="flex justify-end">
                        <Button
                          onClick={handleWorkspaceUpload}
                          disabled={
                            isWorkspaceSubmitting ||
                            !selectedWorkspacePlan ||
                            !workspaceZipFile ||
                            (!selectedWorkspacePlan.uploadedPdfPath?.trim() &&
                              !workspacePdfFile)
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {renderUploadProgress()}
              </div>
            </div>
          )}
        </>
      )}

      {!isZenyum && (
        <div className="mt-4 flex gap-2 items-end">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="mobileOrUid">Mobile Or UID</Label>
            <Input
              id="mobileOrUid"
              value={mobileOrUid}
              onChange={handleTextChange}
            />
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="zipFile">PLY Zip File</Label>
            <Input
              id="zipFile"
              type="file"
              accept="application/zip"
              onChange={handleInputChange}
              ref={fileInputRef}
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="pdfFile">Report PDF File</Label>
            <Input
              id="pdfFile"
              type="file"
              accept="application/pdf"
              onChange={handlePdfFileChange}
              ref={pdfFileInputRef}
            />
          </div>

          <Button onClick={handleUpload} disabled={isSubmitting}>
            Upload
          </Button>
        </div>
      )}

      {renderUploadProgress()}
    </div>
  );
};
